import { motion, useInView, useScroll } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaSeedling, FaCamera, FaTree, FaLeaf, FaRupeeSign } from "react-icons/fa";
import educationImg from "../assets/eduicon.png";
import enovironmentImg from "../assets/environmenticon.png";
import womenImg from "../assets/womenicon.png";
import ruralImg from "../assets/ruralicon.png";
import enterpreneurImg from "../assets/entrepreneuricon.png";
import healthImg from "../assets/healthicon.png";
import communitiesImg from "../assets/communities.avif";
import DonationModal from "../components/DonationModal";


const ImpactItem = ({ value, suffix, label, description }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: 0.4, once: true });
  const [count, setCount] = useState(0);
  

  useEffect(() => {
    if (!isInView) return;

    let start = 0;
    const duration = 1200;
    const increment = Math.ceil(value / (duration / 16));

    const counter = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(counter);
      } else {
        setCount(start);
      }
    }, 16);

    return () => clearInterval(counter);
  }, [isInView, value]);

  return (
    <div ref={ref} className="text-center">
      <h3 className="text-4xl sm:text-5xl font-bold text-green-500 dark:text-green-400">
        {count}{suffix}
      </h3>
      <p className="uppercase tracking-wide text-sm font-semibold mt-2 text-gray-800 dark:text-gray-200">
        {label}
      </p>
      <p className="text-gray-600 text-sm mt-1 max-w-xs mx-auto dark:text-gray-300">
        {description}
      </p>
    </div>
  );
};

/* Animations */
const slideIn = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0 }
};

const cardSlide = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const simpleContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
function ProgramItem({ image, title, desc, titleColor, onClick }) {
  return (
    <motion.div
      variants={slideIn}
      whileHover={{ scale: 1.02 }}
      onClick={onClick}
      className="flex items-start gap-5 cursor-pointer p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
    >
      {/* IMAGE ONLY */}
      <img
        src={image}
        alt={title}
        className="w-14 h-14 object-contain shrink-0"
      />

      {/* TEXT */}
      <div>
        <h3 className={`text-lg font-extrabold ${titleColor}`}>
          {title}
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 max-w-sm">
          {desc}
        </p>
      </div>
    </motion.div>
  );
}

/* Counter */
// function Counter({ value, suffix = "" }) {
//   const [display, setDisplay] = useState(0);
//   const count = useMotionValue(0);

//   useEffect(() => {
//     const controls = animate(count, value, {
//       duration: 2,
//       ease: "easeOut",
//       onUpdate(latest) {
//         setDisplay(Math.round(latest));
//       }
//     });

//     return () => controls.stop();
//   }, [value]);

//   return (
//     <motion.span
//       initial={{ opacity: 0 }}
//       whileInView={{ opacity: 1 }}
//       viewport={{ once: false }}
//     >
//       {display}
//       {suffix}
//     </motion.span>
//   );
// }



export default function Home() {
  const navigate = useNavigate();
  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);

  // Animation for the background floating blobs
  const blobVariants = {
    animate: {
      scale: [1, 1.1, 1],
      rotate: [0, 90, 0],
      transition: {
        duration: 10,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };
  // Animation Variants
  // const line1Variant = {
  //   hidden: { x: "-100vw", opacity: 0 },
  //   visible: {
  //     x: 0,
  //     opacity: 1,
  //     transition: { type: "spring", damping: 20, stiffness: 50 }
  //   }
  // };

  // const line2Variant = {
  //   hidden: { x: "100vw", opacity: 0 },
  //   visible: {
  //     x: 0,
  //     opacity: 1,
  //     transition: { type: "spring", damping: 20, stiffness: 50 }
  //   }
  // };

  const typingContainer = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.03, // Speed of "typing"
      },
    },
  };

  const letterVariant = {
    hidden: { opacity: 0, display: "none" },
    visible: { opacity: 1, display: "inline" },
  };

  const subText = "We drive change through education, sustainability, and innovation. Join us in making a global impact today.";

  const containerRef = useRef(null);

  // 2. Subtext "Erase" effect: Map scroll to width/opacity
  // const textWidth = useTransform(scrollYProgress, [0, 0.4], ["100%", "0%"]);

  // // 3. Background Parallax & Zoom
  // const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.3]);
  // const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <div id="homeTop">
      {/* HERO */}
      <section
        ref={containerRef}
        className="relative h-screen w-full overflow-hidden flex items-center justify-center text-white"
      >
        {/* --- BACKGROUND ANIMATION --- */}
        <motion.div
          initial={{ scale: 1 }}
          animate={{ scale: 1.1 }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear"
          }}
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?auto=format&fit=crop&q=80&w=2070')`, // Replace with your heroImage variable
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}

        >
          {/* Dark Overlay with a subtle moving gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        </motion.div>

        {/* --- CONTENT --- */}
        <div className="relative z-10 text-center px-6 w-full max-w-6xl">

          {/* BIG TEXT: LINE 1 (From Left) */}
          <div className=" mb-2">
            <motion.h1
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="text-5xl md:text-7xl font-black text-white dark:text-green-500 drop-shadow-[0_5px_15px_rgba(0,0,0,0.5)]"
            >
              Empowering Communities
            </motion.h1>
          </div>

          {/* BIG TEXT: LINE 2 (From Right) */}
          <div className="overflow-hidden mb-8">
            <motion.h1
              initial={{ x: 100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
              className="text-5xl md:text-7xl font-black text-green-400 dark:text-white drop-shadow-[0_5px_15px_rgba(34,197,94,0.3)]"
            >
              for a Better Tomorrow
            </motion.h1>
          </div>

          {/* SMALL TEXT (Typewriter Animation) */}


          <motion.div
            variants={typingContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
            className="mx-auto max-w-2xl min-h-[3rem] text-center"
          // Prevents layout shift
          >
            <p className="text-lg md:text-xl text-gray-100 dark:text-green-500 font-medium leading-relaxed drop-shadow-lg">
              {subText.split("").map((char, index) => (
                <motion.span key={index} variants={letterVariant}>
                  {char}
                </motion.span>
              ))}
              {/* The "Blinking Cursor" */}
              <motion.span
                animate={{ opacity: [0, 1, 0] }}
                transition={{ repeat: Infinity, duration: 1 }}
                className="inline-block w-[2px] h-5 bg-green-400 ml-1 align-middle"
              />
            </p>
          </motion.div>


          {/* CTA BUTTONS */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <motion.button
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.6 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
              whileHover={{ scale: 1.05, backgroundColor: "#22c55e" }}
              whileTap={{ scale: 0.95 }}
              onClick={()=>{
                const section=document.getElementById("programs");
                if(section){
                  section.scrollIntoView({behavior:"smooth"});
                }
              }}
              className="bg-green-500 dark:text-black text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-widest shadow-[0_0_20px_rgba(34,197,94,0.5)] transition-all"
            >
              Explore programs
            </motion.button>
            
            <motion.button
              initial={{ y: 40, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: false, amount: 0.6 }}
              transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
              whileHover={{ scale: 1.05, backgroundColor: "#ffffff", color: "#22c55e" }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsDonationModalOpen(true)}
              className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-bold text-lg uppercase tracking-widest hover:bg-white hover:text-green-500 transition-all"
            >
              Donate Now
            </motion.button>
          </div>
        </div>

        {/* Decorative Floating Elements for visual "Attractive" look */}
        <motion.div
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity }}
          className="absolute bottom-20 left-10 w-32 h-32 bg-green-500/10 rounded-full blur-3xl"
        />
      </section>

      {/* WHY */}
      <section className="py-20 bg-white dark:bg-gray-900 px-6">
        <motion.div
          variants={slideIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-green-400">Why We Exist</h2>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            Millions of people lack access to quality education, sustainable
            livelihoods, and basic environmental protection. We exist to bridge
            this gap by empowering communities with scalable solutions.
          </p>
        </motion.div>
      </section>

      {/* IMPACT */}

      <section className="bg-gray-100 dark:bg-gray-800 py-16 px-4">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ amount: 0.3, once: true }}
          className="max-w-7xl mx-auto"
        >
          <h2 className="text-center text-3xl sm:text-4xl font-bold mb-12 text-gray-900 dark:text-green-400">
            Our Impact
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
            <ImpactItem
              value={2000}
              suffix="+"
              label="Children"
              description="Children and families impacted every year"
            />
            <ImpactItem
              value={20}
              suffix="+"
              label="Villages"
              description="Villages and slums reached nationwide"
            />
            <ImpactItem
              value={400}
              suffix="+"
              label="Projects"
              description="Projects in education, health & empowerment"
            />
            <ImpactItem
              value={27}
              suffix="+"
              label="States"
              description="States including remote regions"
            />
          </div>
        </motion.div>
      </section>

      {/* TREE PLANTING INITIATIVE */}
      <section className="py-20 px-6 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${communitiesImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-600/90 via-emerald-600/90 to-teal-600/90" />
        </div>
        
        {/* Background decorative elements */}
        <div className="absolute inset-0 opacity-10 z-10">
          <FaSeedling className="absolute top-10 left-10 text-8xl text-white" />
          <FaTree className="absolute bottom-10 right-10 text-8xl text-white" />
          <FaLeaf className="absolute top-1/2 left-1/4 text-6xl text-white" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ amount: 0.3, once: true }}
          className="max-w-6xl mx-auto text-center relative z-20 text-white"
        >
          <motion.div
            initial={{ scale: 0.8 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-block mb-6"
          >
            <FaLeaf className="text-6xl" />
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Plant a Tree, Make a Difference
          </h2>
          
          <p className="text-xl text-green-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Your donation helps us plant trees and restore our environment. 
            <span className="font-semibold text-white">We'll plant a tree for every donation</span> and send you 
            <span className="font-semibold text-white"> confidential proof with photos and location details.</span>
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
            >
              <FaRupeeSign className="text-4xl mb-4 mx-auto" />
              <h3 className="text-xl font-bold mb-2">Donate Any Amount</h3>
              <p className="text-green-100 text-sm">Every contribution counts towards environmental restoration</p>
            </motion.div>
            
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
            >
              <FaSeedling className="text-4xl mb-4 mx-auto" />
              <h3 className="text-xl font-bold mb-2">We Plant Trees</h3>
              <p className="text-green-100 text-sm">Our team plants native trees in areas that need reforestation</p>
            </motion.div>
            
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
            >
              <FaCamera className="text-4xl mb-4 mx-auto" />
              <h3 className="text-xl font-bold mb-2">Get Proof</h3>
              <p className="text-green-100 text-sm">Receive photos and GPS coordinates of your planted tree</p>
            </motion.div>
          </div>

          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: "#ffffff", color: "#059669" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsDonationModalOpen(true)}
            className="bg-white text-green-600 px-8 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-2 mx-auto"
          >
            <FaTree className="text-xl" /> Plant Your Tree Today
          </motion.button>
          
          <p className="mt-4 text-sm text-green-200">
            All tree planting activities are documented and shared confidentially with donors
          </p>
        </motion.div>
      </section>

      {/* OUR PROGRAMS (VERTICAL) */}
      <section id="programs" className="py-10 px-6 bg-white dark:bg-gray-900">
  <motion.div
    variants={slideIn}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="max-w-4xl mx-auto text-center mb-20"
  >
    <h2 className="text-4xl font-bold text-gray-900 dark:text-green-400">Our Programs</h2>
    <p className="mt-4 text-gray-600 dark:text-gray-300">
      We work across key areas to create sustainable, long-term impact at the grassroots level.
    </p>
  </motion.div>

  <motion.div
    variants={simpleContainer}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    className="max-w-6xl mx-auto grid md:grid-cols-2 gap-x-24 gap-y-16"
  >

    {/* LEFT COLUMN */}
    <div className="space-y-16">
      <ProgramItem
       image={enovironmentImg}
        title="ENVIRONMENT & CLIMATE ACTION"
        titleColor="text-green-300 dark:text-green-300"
        desc="Promoting sustainability, conservation, and climate resilience within communities."
        onClick={() => navigate("/environment")}
      />

      <ProgramItem
       image={enterpreneurImg}
        title="ENTREPRENEURSHIP & INNOVATION"
        titleColor="text-blue-300 dark:text-blue-300"
        desc="Supporting entrepreneurs and startups to create inclusive economic opportunities."
        onClick={() => navigate("/entrepreneurship")}
      />

      <ProgramItem
  image={educationImg}
  title="EDUCATION & SKILL DEVELOPMENT"
  titleColor="text-pink-300 dark:text-pink-300"
  desc="Enhancing learning outcomes and employability through education and training."
  onClick={() => navigate("/education")}
/>



    </div>

    {/* RIGHT COLUMN */}
    <div className="space-y-16">
      <ProgramItem
        image={healthImg}
        titleColor="text-purple-300 dark:text-purple-300"
        title="HEALTH & HYGIENE"
        desc="Improving healthcare access and hygiene awareness in underserved communities."
        onClick={() => navigate("/health")}
      />

      <ProgramItem
        image={womenImg}
        titleColor="text-pink-400 dark:text-pink-400"
        title="WOMEN EMPOWERMENT"
        desc="Empowering women through education, leadership, and community participation."
        onClick={() => navigate("/women-empowerment")}
      />

      <ProgramItem
        image={ruralImg}
        titleColor="text-orange-300 dark:text-orange-400"
        title="RURAL DEVELOPMENT & LIVELIHOOD"
        desc="Strengthening rural economies and sustainable livelihood opportunities."
        onClick={() => navigate("/rural-development")}
      />
    </div>
  </motion.div>
</section>

      {/* BLOGS */}
      <section className="py-12 px-6 bg-gray-100 dark:bg-gray-800">
        <motion.div
          variants={slideIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-green-400">Latest Blogs</h2>
          <p className="mt-4 text-gray-600 dark:text-gray-300">
            Insights, stories, and updates from our work on the ground.
          </p>
        </motion.div>

        <motion.div
          variants={simpleContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {[
            {
              title: "Building Sustainable Communities",
              date: "March 2025",
              desc: "How grassroots initiatives are transforming rural livelihoods.",
              adr:"/SustainableCommunitiesBlog",
            },
            {
              title: "Education as a Catalyst for Change",
              date: "February 2025",
              desc: "Why skill-based education matters more than ever.",
              adr:"/educationBlog",
            },
            {
              title: "Women Leading the Way",
              date: "January 2025",
              desc: "Stories of women creating impact in their communities.",
              adr:"/WomenLeadingTheWayBlog",
            },
            {
              title: "Innovation in Social Development",
              date: "December 2024",
              desc: "Using technology to solve real-world problems.",
              adr:"/innovationBlog",
            },
            {
              title: "Health & Hygiene Awareness",
              date: "November 2024",
              desc: "Small habits that lead to healthier communities.",
              adr:"/healthAwarenessBlog",
            },
            {
              title: "Climate Action at the Local Level",
              date: "October 2024",
              desc: "How local actions contribute to global impact.",
              adr:"/climateactionBlog",
            }
          ].map((blog, index) => (
            <motion.div
              key={index}
              variants={cardSlide}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white dark:bg-gray-900 rounded-xl shadow-md p-6 hover:shadow-xl transition-shadow cursor-pointer"
              onClick={() => navigate(blog.adr)}
            >
              <p className="text-sm text-green-600 dark:text-green-400 font-semibold">{blog.date}</p>
              <h3 className="mt-3 text-xl font-bold dark:text-white">{blog.title}</h3>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm">
                {blog.desc}
              </p>

              <button className="mt-4 text-green-600 dark:text-green-400 font-semibold hover:underline">
                Read More →
              </button>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* CTA */}
     <section className="relative overflow-hidden bg-white dark:bg-gray-900 py-14 md:py-32 px-6">
      
      {/*  ANIMATED BACKGROUND ELEMENTS */}
      <div className="absolute inset-0 z-0 opacity-20 dark:opacity-30 pointer-events-none">
        <motion.div 
          variants={blobVariants}
          animate="animate"
          className="absolute -top-20 -left-20 w-96 h-96 bg-green-400 rounded-full blur-[100px]"
        />
        <motion.div 
          variants={blobVariants}
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            transition: { duration: 8, repeat: Infinity }
          }}
          className="absolute -bottom-20 -right-20 w-[500px] h-[500px] bg-emerald-500 rounded-full blur-[120px]"
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* SMALL TOP LABEL */}
        <motion.span 
          initial={{ opacity: 0, letterSpacing: "0.1em" }}
          whileInView={{ opacity: 1, letterSpacing: "0.3em" }}
          className="inline-block uppercase text-sm font-bold text-green-600 dark:text-green-400 mb-6 tracking-widest"
        >
          Our Shared Future
        </motion.span>

        {/* MAIN HEADLINE WITH STAGGERED REVEAL */}
        <motion.h2 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white leading-[0.9] mb-8"
        >
          BE THE <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-400">CHANGE</span> <br />
          YOU WISH TO SEE.
        </motion.h2>

        {/* SUPPORTING TEXT */}
        <motion.p 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-xs md:text-sm text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed"
        >
          We aren't just building projects, we are building hope. Join 5,000+ volunteers 
          and donors dedicated to creating a sustainable and inclusive future for all.
        </motion.p>

        {/* HIGH-END INTERACTIVE BUTTON */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 100 }}
        >
          <button className="group relative px-10 py-5 bg-green-600 text-white font-bold rounded-full overflow-hidden transition-all hover:pr-14 active:scale-95 shadow-xl shadow-green-500/20
          " onClick={()=>navigate("/contact")}>
            <span className="relative z-10">Start Your Journey Today</span>
            <span className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all">
              →
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-green-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </button>
        </motion.div>

        {/* BOTTOM DECORATION: STATS OR SOCIAL PROOF */}
        <div className="mt-20 flex flex-wrap justify-center gap-8 md:gap-16 border-t border-green-200 dark:border-green-900 pt-12">
           {[
             { label: "Community Support", val: "98%" },
             { label: "Transparency Score", val: "A+" },
             { label: "Global Reach", val: "14 countries" }
           ].map((item, i) => (
             <motion.div 
               key={i}
               initial={{ opacity: 0, y: 20 }}
               whileInView={{ opacity: 1, y: 0 }}
               transition={{ delay: 0.2 * i }}
               className="text-center"
             >
               <p className="text-3xl font-bold text-gray-900 dark:text-white">{item.val}</p>
               <p className="text-xs uppercase tracking-tighter text-gray-400">{item.label}</p>
             </motion.div>
           ))}
        </div>
      </div>
    </section>

      {/* Donation Modal */}
      <DonationModal 
        isOpen={isDonationModalOpen} 
        onClose={() => setIsDonationModalOpen(false)} 
      />

    </div>
  );
}